// components/profile/PersonalInformation.tsx
import React, { useState, useEffect, useMemo } from "react";
import {
  User,
  Mail,
  Phone,
  Edit3,
  Save,
  X,
  Loader2,
  Calendar,
  AlertCircle,
  CheckCircle2,
} from "lucide-react";
import { format, isValid } from "date-fns";
import DatePicker from "./DatePicker";

interface PersonalInfo {
  firstName: string;
  lastName: string;
  middleName: string;
  phoneNumber: string;
  email: string;
  dateOfBirth: string;
}

interface PersonalInformationProps {
  data: PersonalInfo;
  onSave: (data: PersonalInfo) => Promise<boolean>;
  isPending: boolean;
  highlightDob?: boolean;
  isAnotherSectionEditing?: boolean;
  onEditingChange?: (isEditing: boolean) => void;
}

const PersonalInformation: React.FC<PersonalInformationProps> = ({
  data,
  onSave,
  isPending,
  highlightDob = false,
  isAnotherSectionEditing = false,
  onEditingChange,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState(data);

  // Update formData when data prop changes
  useEffect(() => {
    if (!isEditing) {
      setFormData(data);
    }
  }, [data, isEditing]);

  // Notify parent about editing state changes
  useEffect(() => {
    onEditingChange?.(isEditing);
  }, [isEditing, onEditingChange]);

  // Auto-open edit mode if DOB needs to be highlighted and is missing
  useEffect(() => {
    if (highlightDob && !data.dateOfBirth && !isAnotherSectionEditing) {
      setIsEditing(true);
    }
  }, [highlightDob, data.dateOfBirth, isAnotherSectionEditing]);

  // Check if date of birth is missing
  const isDOBMissing = useMemo(() => {
    if (!formData.dateOfBirth) return true;
    const date = new Date(formData.dateOfBirth);
    return !isValid(date);
  }, [formData.dateOfBirth]);

  // Check if form is valid for submission
  const isFormValid = useMemo(() => {
    return (
      formData.firstName.trim() !== "" &&
      formData.lastName.trim() !== "" &&
      formData.phoneNumber.trim() !== "" &&
      !isDOBMissing
    );
  }, [formData, isDOBMissing]);

  // Check if form has changes
  const hasChanges = useMemo(() => {
    return (
      formData.firstName !== data.firstName ||
      formData.lastName !== data.lastName ||
      formData.middleName !== data.middleName ||
      formData.phoneNumber !== data.phoneNumber ||
      formData.dateOfBirth !== data.dateOfBirth
    );
  }, [formData, data]);

  // Missing fields for validation
  const missingFields = useMemo(() => {
    const fields: string[] = [];
    if (!formData.firstName.trim()) fields.push("First Name");
    if (!formData.lastName.trim()) fields.push("Last Name");
    if (!formData.phoneNumber.trim()) fields.push("Phone Number");
    if (isDOBMissing) fields.push("Date of Birth");
    return fields;
  }, [formData, isDOBMissing]);

  const handleChange = (field: keyof PersonalInfo, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!isFormValid || isPending) return;
    const success = await onSave(formData);
    if (success) {
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setFormData(data);
    setIsEditing(false);
  };

  const handleStartEditing = () => {
    if (isAnotherSectionEditing) return;
    setIsEditing(true);
  };

  const formatDisplayDate = (dateString: string) => {
    if (!dateString) return "Not set";
    const date = new Date(dateString);
    if (!isValid(date)) return "Not set";
    return format(date, "MMMM d, yyyy");
  };

  return (
    <div
      id="personal-info-section"
      className={`bg-white rounded-2xl shadow-sm border transition-all duration-300 ${
        isEditing
          ? "ring-2 ring-[#8B52FF] border-[#8B52FF]"
          : "border-gray-100 hover:shadow-md"
      }`}
    >
      {/* Header */}
      <div className="p-4 sm:p-6 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 sm:gap-4">
            <div
              className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center transition-all ${
                isEditing
                  ? "bg-gradient-to-br from-[#8B52FF] to-[#E05A6A]"
                  : "bg-gradient-to-br from-[#8B52FF]/10 to-[#E05A6A]/10"
              }`}
            >
              <User
                className={`w-5 h-5 sm:w-6 sm:h-6 ${
                  isEditing ? "text-white" : "text-[#8B52FF]"
                }`}
              />
            </div>
            <div>
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
                Personal Information
              </h2>
              <p className="text-xs sm:text-sm text-gray-500">
                Your basic personal details
              </p>
            </div>
          </div>

          {/* Desktop Edit Button */}
          {!isEditing && (
            <button
              onClick={handleStartEditing}
              disabled={isAnotherSectionEditing}
              className={`hidden sm:flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-all ${
                isAnotherSectionEditing
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              <Edit3 className="w-4 h-4" />
              Edit
            </button>
          )}
        </div>

        {/* Editing Mode Banner */}
        {isEditing && (
          <div className="mt-4 p-3 bg-[#8B52FF]/5 border border-[#8B52FF]/20 rounded-xl">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-[#8B52FF] rounded-full animate-pulse" />
              <span className="text-sm font-medium text-[#8B52FF]">
                Editing Mode
              </span>
            </div>
          </div>
        )}

        {/* DOB Missing Warning (when not editing) */}
        {!isEditing && isDOBMissing && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-xl">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-800">
                  Date of Birth Required
                </p>
                <p className="text-xs text-red-600 mt-0.5">
                  Required for SSN and document verification
                </p>
              </div>
              <button
                onClick={handleStartEditing}
                disabled={isAnotherSectionEditing}
                className="text-sm font-medium text-red-700 hover:text-red-800 underline"
              >
                Add Now
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Form Content */}
      <div className="p-4 sm:p-6">
        {/* Mobile Edit Button */}
        {!isEditing && (
          <button
            onClick={handleStartEditing}
            disabled={isAnotherSectionEditing}
            className={`sm:hidden w-full mb-4 flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-medium transition-all ${
              isAnotherSectionEditing
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            <Edit3 className="w-4 h-4" />
            Edit Personal Information
          </button>
        )}

        {/* Fields Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
          {/* First Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
              First Name <span className="text-red-500">*</span>
            </label>
            {isEditing ? (
              <div className="relative">
                <div className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 pointer-events-none">
                  <User className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => handleChange("firstName", e.target.value)}
                  disabled={isPending}
                  placeholder="Enter first name"
                  className={`w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-2.5 sm:py-3 text-sm sm:text-base border-2 rounded-xl transition-all outline-none disabled:opacity-50 ${
                    !formData.firstName.trim()
                      ? "border-red-200 focus:border-red-400 focus:ring-2 focus:ring-red-100"
                      : "border-gray-200 focus:border-[#8B52FF] focus:ring-2 focus:ring-[#8B52FF]/20"
                  }`}
                />
                {!formData.firstName.trim() && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <AlertCircle className="w-4 h-4 text-red-400" />
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-3 p-3 sm:p-4 rounded-xl bg-gray-50 border border-gray-100">
                <User className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                <span className="text-sm sm:text-base text-gray-900">
                  {formData.firstName || "Not set"}
                </span>
              </div>
            )}
          </div>

          {/* Last Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
              Last Name <span className="text-red-500">*</span>
            </label>
            {isEditing ? (
              <div className="relative">
                <div className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 pointer-events-none">
                  <User className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => handleChange("lastName", e.target.value)}
                  disabled={isPending}
                  placeholder="Enter last name"
                  className={`w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-2.5 sm:py-3 text-sm sm:text-base border-2 rounded-xl transition-all outline-none disabled:opacity-50 ${
                    !formData.lastName.trim()
                      ? "border-red-200 focus:border-red-400 focus:ring-2 focus:ring-red-100"
                      : "border-gray-200 focus:border-[#8B52FF] focus:ring-2 focus:ring-[#8B52FF]/20"
                  }`}
                />
                {!formData.lastName.trim() && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <AlertCircle className="w-4 h-4 text-red-400" />
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-3 p-3 sm:p-4 rounded-xl bg-gray-50 border border-gray-100">
                <User className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                <span className="text-sm sm:text-base text-gray-900">
                  {formData.lastName || "Not set"}
                </span>
              </div>
            )}
          </div>

          {/* Middle Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
              Middle Name{" "}
              <span className="text-gray-400 font-normal">(Optional)</span>
            </label>
            {isEditing ? (
              <div className="relative">
                <div className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 pointer-events-none">
                  <User className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={formData.middleName}
                  onChange={(e) => handleChange("middleName", e.target.value)}
                  disabled={isPending}
                  placeholder="Enter middle name"
                  className="w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-2.5 sm:py-3 text-sm sm:text-base border-2 border-gray-200 rounded-xl focus:border-[#8B52FF] focus:ring-2 focus:ring-[#8B52FF]/20 transition-all outline-none disabled:opacity-50"
                />
              </div>
            ) : (
              <div className="flex items-center gap-3 p-3 sm:p-4 rounded-xl bg-gray-50 border border-gray-100">
                <User className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                <span className="text-sm sm:text-base text-gray-900">
                  {formData.middleName || "Not set"}
                </span>
              </div>
            )}
          </div>

          {/* Phone Number */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
              Phone Number <span className="text-red-500">*</span>
            </label>
            {isEditing ? (
              <div className="relative">
                <div className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 pointer-events-none">
                  <Phone className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                </div>
                <input
                  type="tel"
                  value={formData.phoneNumber}
                  onChange={(e) => handleChange("phoneNumber", e.target.value)}
                  disabled={isPending}
                  placeholder="Enter phone number"
                  className={`w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-2.5 sm:py-3 text-sm sm:text-base border-2 rounded-xl transition-all outline-none disabled:opacity-50 ${
                    !formData.phoneNumber.trim()
                      ? "border-red-200 focus:border-red-400 focus:ring-2 focus:ring-red-100"
                      : "border-gray-200 focus:border-[#8B52FF] focus:ring-2 focus:ring-[#8B52FF]/20"
                  }`}
                />
                {!formData.phoneNumber.trim() && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <AlertCircle className="w-4 h-4 text-red-400" />
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-3 p-3 sm:p-4 rounded-xl bg-gray-50 border border-gray-100">
                <Phone className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                <span className="text-sm sm:text-base text-gray-900">
                  {formData.phoneNumber || "Not set"}
                </span>
              </div>
            )}
          </div>

          {/* Date of Birth */}
          <div
            className={`${
              highlightDob && isDOBMissing && isEditing
                ? "sm:col-span-2 p-4 bg-amber-50 rounded-xl border border-amber-200"
                : ""
            }`}
          >
            {highlightDob && isDOBMissing && isEditing && (
              <div className="flex items-center gap-2 mb-3">
                <AlertCircle className="w-4 h-4 text-amber-600" />
                <span className="text-sm font-medium text-amber-700">
                  Required for verification
                </span>
              </div>
            )}
            <label className="block text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
              Date of Birth <span className="text-red-500">*</span>
            </label>
            {isEditing ? (
              <DatePicker
                value={formData.dateOfBirth}
                onChange={(date) => handleChange("dateOfBirth", date)}
                disabled={isPending}
                placeholder="Select date of birth"
              />
            ) : (
              <div
                className={`flex items-center gap-3 p-3 sm:p-4 rounded-xl border ${
                  isDOBMissing
                    ? "bg-red-50 border-red-200"
                    : "bg-gray-50 border-gray-100"
                }`}
              >
                <Calendar
                  className={`w-4 h-4 sm:w-5 sm:h-5 ${
                    isDOBMissing ? "text-red-400" : "text-gray-400"
                  }`}
                />
                <span
                  className={`text-sm sm:text-base ${
                    isDOBMissing ? "text-red-600" : "text-gray-900"
                  }`}
                >
                  {formatDisplayDate(formData.dateOfBirth)}
                </span>
              </div>
            )}
            {!isEditing && isDOBMissing && (
              <p className="mt-1.5 text-xs text-red-600">
                Required for SSN and document verification
              </p>
            )}
          </div>

          {/* Email - Full Width, Read Only */}
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
              Email Address <span className="text-red-500">*</span>
            </label>
            <div className="flex items-center gap-3 p-3 sm:p-4 rounded-xl bg-gray-50 border border-gray-100">
              <Mail className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
              <span className="text-sm sm:text-base text-gray-900 flex-1">
                {formData.email}
              </span>
              {isEditing && (
                <span className="text-xs text-gray-400 bg-gray-200 px-2 py-1 rounded">
                  Cannot be changed
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Inline Validation Messages (when editing) */}
        {isEditing && missingFields.length > 0 && (
          <div className="mt-4 p-3 bg-red-50 border border-red-100 rounded-xl">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-700">
                  Required fields missing:
                </p>
                <p className="text-xs text-red-600 mt-1">
                  {missingFields.join(", ")}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons (Attached to Component) */}
        {isEditing && (
          <div className="mt-6 pt-4 border-t border-gray-100">
            {/* Change Status Indicator */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                {hasChanges ? (
                  <>
                    <div className="w-2 h-2 bg-amber-500 rounded-full" />
                    <span className="text-sm text-amber-600 font-medium">
                      Unsaved changes
                    </span>
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                    <span className="text-sm text-green-600">
                      No changes made
                    </span>
                  </>
                )}
              </div>

              <div className="text-xs text-gray-500">
                {4 - missingFields.length}/4 required fields complete
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleCancel}
                disabled={isPending}
                className="flex-1 sm:flex-none px-6 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <X className="w-4 h-4" />
                Cancel
              </button>

              <button
                onClick={handleSave}
                disabled={!isFormValid || isPending || !hasChanges}
                className={`flex-1 px-6 py-3 rounded-xl font-medium transition-all flex items-center justify-center gap-2 ${
                  isFormValid && hasChanges && !isPending
                    ? "bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] text-white hover:opacity-90 shadow-lg shadow-[#8B52FF]/25"
                    : "bg-gray-100 text-gray-400 cursor-not-allowed"
                }`}
              >
                {isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Save Changes
                  </>
                )}
              </button>
            </div>

            {/* Helper Text */}
            {!hasChanges && (
              <p className="text-xs text-gray-500 text-center mt-3">
                Make changes to enable the save button
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PersonalInformation;
